import { chainWalk } from '../../../../../../utils/helpers';
import { findTotalVolumeFromDemand, getBacklogChargeValueByRoleName, getCapacity, getScheduled } from './commonUtils';
import { GROCERY_ROLE_MAPPINGS, SSD_VOLUME_SOURCES } from './../../../../../../utils/constants';

const getGroceryVolume = (outboundVolume, groceryCapacitySplit) => {
  const calculateSplitValues = {};
  const overrideCapacityPercentage = chainWalk(
    () => outboundVolume.overrides?.ambient?.overrideKeyValueMap?.atropsOverrideCapacityPercentage,
    100
  );

  const createCategory = (roleName) => {
    const atropsScheduled = getScheduled(outboundVolume);
    const atropsCapacity = getCapacity(outboundVolume);

    // Formula for Grocery charge:
    // 1.  Calculate adjusted capacity using the override percentage (90 for 90%):
    const adjustedAtropsCapacity = (atropsCapacity * Number(overrideCapacityPercentage)) / 100;
    // 2. Subtract scheduled amount and clamp at zero:
    const capacityDiff = Math.max(adjustedAtropsCapacity - atropsScheduled, 0);
    // 3. Multiply by the role split (percentage) defined in ADMIN settings, and divide by 100 to convert that percentage:
    const roleSplit = groceryCapacitySplit[roleName] || 0; // e.g., 30 for 30%
    const planningCharge = (capacityDiff * roleSplit) / 100;

    const rodeoRoleNames = GROCERY_ROLE_MAPPINGS[roleName].rodeoRoleNames;
    // Extensible with future Rodeo process role names
    const rodeoBacklog = rodeoRoleNames
      .map((rodeoRoleName) => getBacklogChargeValueByRoleName(outboundVolume, rodeoRoleName))
      .reduce((a, b) => a + b, 0);

    return { planningCharge, rodeoBacklog };
  };

  // This used to be chilled so ppchilled would include chilled. Now is pick_grocery_chilled, so need to either change
  // frontend logic or backend. Would prefer to change frontend
  Object.entries(groceryCapacitySplit).forEach(([processType, _]) => {
    if (processType in GROCERY_ROLE_MAPPINGS) {
      calculateSplitValues[processType] = createCategory(processType);
    }
  });

  const total = Object.values(calculateSplitValues).reduce(
    (acc, val) => {
      acc.planningCharge += Number(val.planningCharge) || 0;
      acc.rodeoBacklog += Number(val.rodeoBacklog) || 0;
      return acc;
    },
    {
      planningCharge: 0,
      rodeoBacklog: 0
    }
  );

  return { total, ...calculateSplitValues };
};

export const getGroceryVolumesForOverrides = (outboundVolume, groceryCapacitySplit) => {
  const allVolumes = getGroceryVolume(outboundVolume, groceryCapacitySplit);

  return {
    ...allVolumes
  };
};

export const getGroceryVolumesFromDemand = (demand, cptDateTimeLocal) => {
  const ambientPredicted = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick_grocery_ambient'],
    [SSD_VOLUME_SOURCES.ATROPS]
  );
  const ambientBacklog = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick_grocery_ambient'],
    [SSD_VOLUME_SOURCES.RODEO]
  );
  const chillPredicted = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick_grocery_chilled'],
    [SSD_VOLUME_SOURCES.ATROPS]
  );
  const chillBacklog = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick_grocery_chilled'],
    [SSD_VOLUME_SOURCES.RODEO]
  );
  const frozenPredicted = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick_grocery_frozen'],
    [SSD_VOLUME_SOURCES.ATROPS]
  );
  const frozenBacklog = findTotalVolumeFromDemand(
    demand,
    cptDateTimeLocal,
    ['outbound'],
    ['pick_grocery_frozen'],
    [SSD_VOLUME_SOURCES.RODEO]
  );

  return {
    ambient: { planningCharge: ambientPredicted, rodeoBacklog: ambientBacklog },
    chilled: { planningCharge: chillPredicted, rodeoBacklog: chillBacklog },
    frozen: { planningCharge: frozenPredicted, rodeoBacklog: frozenBacklog },
    total: {
      planningCharge: ambientPredicted + chillPredicted + frozenPredicted,
      rodeoBacklog: ambientBacklog + chillBacklog + frozenBacklog
    }
  };
};
